import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../axiosConfig";
import usePermiso from "../../hooks/usePermiso";

function pad(n) {
  return String(n).padStart(2, "0");
}

function fechaISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtSabado(fecha) {
  const d = new Date(`${fecha}T12:00:00`);
  return d.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getISOWeekNumber(fechaStr) {
  const [y, m, d] = fechaStr.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

function sabadoSemanaActual(ref = new Date()) {
  const d = new Date(ref);
  d.setHours(12, 0, 0, 0);
  const dow = d.getDay();
  if (dow === 6) return fechaISO(d);
  if (dow === 0) {
    d.setDate(d.getDate() - 1);
    return fechaISO(d);
  }
  d.setDate(d.getDate() + (6 - dow));
  return fechaISO(d);
}

function filaScrollObjetivo(rows) {
  if (!rows?.length) return null;
  const hoy = fechaISO(new Date());
  const satActual = sabadoSemanaActual();
  if (rows.some((r) => r.fecha === satActual)) return satActual;
  const futuro = rows.find((r) => r.fecha >= hoy);
  if (futuro) return futuro.fecha;
  return rows[rows.length - 1].fecha;
}

export default function ModalGuardiasSabados({ isOpen, onClose, theme, onGuardiasChanged }) {
  const { puede } = usePermiso();
  const puedeVerAno = puede("ag_guardia_sab_ano", "ver");
  const puedeModificarStaff = puede("ag_guardia_sab_staff", "modificar");
  const puedeModificarAgentes = puede("ag_guardia_sab_agentes", "modificar");
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ staff: [], agentes: [], excluidos: [], anchor_fecha: "" });
  const [loading, setLoading] = useState(false);
  const [savingFecha, setSavingFecha] = useState(null);
  const [error, setError] = useState("");
  const filaActualRef = useRef(null);
  const sabadoActual = sabadoSemanaActual();

  const inp = {
    width: "100%",
    background: isDark ? c.bgInput : "#ffffff",
    border: `1px solid ${isDark ? c.border : "#d0d0c8"}`,
    borderRadius: 7,
    padding: "5px 8px",
    color: c.textPrimary,
    fontSize: 12,
    boxSizing: "border-box",
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [mRes, lRes] = await Promise.all([
        api.get("/guardias-sabado/meta"),
        api.get("/guardias-sabado", { params: { year } }),
      ]);
      setMeta(mRes.data?.data || { staff: [], agentes: [], excluidos: [] });
      setRows(lRes.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error cargando guardias");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    if (!isOpen) return;
    cargar();
  }, [isOpen, cargar]);

  useEffect(() => {
    if (!isOpen || loading || rows.length === 0) return;
    const id = requestAnimationFrame(() => {
      filaActualRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, loading, rows, year]);

  const guardarFila = async (row, staffId, agentIds) => {
    setSavingFecha(row.fecha);
    setError("");
    try {
      const res = await api.put(`/guardias-sabado/${row.fecha}`, {
        staff_agente_id: staffId,
        agentes_trabajo: agentIds.filter(Boolean),
      });
      setRows(res.data?.data || []);
      onGuardiasChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error guardando");
    } finally {
      setSavingFecha(null);
    }
  };

  const onStaffChange = (row, staffId) => {
    if (!staffId) {
      setError("Staff obligatorio. Elige Angel o Maribel.");
      return;
    }
    const agentIds = row.agentes_trabajo || [];
    guardarFila(row, staffId, agentIds);
  };

  const onAgentChange = (row, idx, agentId) => {
    if (!row.staff_agente_id) {
      setError("Elige Staff antes de cambiar los agentes de guardia.");
      return;
    }
    const ids = [...(row.agentes_trabajo || [])];
    while (ids.length < 2) ids.push("");
    ids[idx] = agentId || "";
    const filtered = ids.filter(Boolean).slice(0, 2);
    guardarFila(row, row.staff_agente_id, filtered);
  };

  const filaObjetivo = filaScrollObjetivo(rows);

  const textoCeldaTabla = "#111827";
  const textoEditado = "#b45309";

  const getRowBg = (idx, esSemanaActual) => {
    if (esSemanaActual) return isDark ? "#16a34a" : "#4ade80";
    return idx % 2 === 0
      ? (isDark ? "#fdba74" : "#ffedd5")
      : (isDark ? "#7dd3fc" : "#dbeafe");
  };

  const headBg = isDark ? c.bgInput : "#e8e8e0";
  const headSep = isDark ? c.border : "#c8c8be";
  const thHead = {
    padding: "8px 6px",
    fontWeight: 600,
    background: headBg,
    position: "sticky",
    top: 0,
    zIndex: 2,
    borderBottom: `1px solid ${headSep}`,
  };

  if (!isOpen) return null;

  const staffOpts = meta.staff || [];
  const agentOpts = meta.agentes || [];
  const staffIds = new Set(staffOpts.map((a) => a.id));
  const filasSinStaff = rows.filter((r) => !r.staff_agente_id || !staffIds.has(r.staff_agente_id));
  const yearOptions = [year - 1, year, year + 1];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#00000088",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: isDark ? c.bgPanel : "#fafaf6",
          borderRadius: 12,
          width: "min(960px, 96vw)",
          maxHeight: "min(90vh, 820px)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          border: `1px solid ${isDark ? c.border : "#d8d8d0"}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px",
            borderBottom: `1px solid ${isDark ? c.border : "#d8d8d0"}`,
          }}
        >
          <div>
            <span style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary }}>📋 Guardias sábados</span>
            <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
              Staff: Angel ↔ Maribel · Agentes: Rita ↔ Josefa+Sabrina · ancla {meta.anchor_fecha || "2026-05-16"}
              {meta.excluidos?.length > 0 && (
                <> · sin sábados: {meta.excluidos.map((a) => a.nombre).join(", ")}</>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", color: c.textMuted, fontSize: 22, cursor: "pointer" }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {puedeVerAno && (
            <>
              <label style={{ fontSize: 12, color: c.textSecondary }}>Año</label>
              <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} style={{ ...inp, width: 100 }}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </>
          )}
          {loading && <span style={{ fontSize: 12, color: c.textMuted }}>Cargando…</span>}
          {savingFecha && (
            <span style={{ fontSize: 12, color: isDark ? "#fcd34d" : "#b45309" }}>
              Guardando {fmtSabado(savingFecha)}…
            </span>
          )}
          {meta.staff?.length < 2 && (
            <span style={{ fontSize: 11, color: "#b45309" }}>
              Falta staff en agentes (se esperan Angel y Maribel).
            </span>
          )}
          {!loading && filasSinStaff.length > 0 && (
            <span style={{ fontSize: 11, color: "#b45309" }}>
              {filasSinStaff.length} sábado(s) sin Staff — al abrir se reparan automáticamente; si persiste, elige Staff y guarda.
            </span>
          )}
        </div>

        {error && (
          <div style={{ margin: "0 18px 8px", padding: "8px 12px", borderRadius: 8, background: "#fef2f2", color: "#b91c1c", fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto", padding: "0 18px 12px", minHeight: 0 }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
            <thead>
              <tr style={{ color: c.textMuted, textAlign: "left" }}>
                <th style={{ ...thHead, width: 44 }} title="Semana del año (ISO)">Sem.</th>
                <th style={thHead}>Sábado</th>
                <th style={thHead}>Staff</th>
                <th style={thHead}>Agente 1</th>
                <th style={thHead}>Agente 2</th>
                <th style={thHead}>No trabajan</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const ag0 = row.agentes_trabajo?.[0] || "";
                const ag1 = row.agentes_trabajo?.[1] || "";
                const isSaving = savingFecha === row.fecha;
                const esSemanaActual = row.fecha === sabadoActual;
                const esScroll = row.fecha === filaObjetivo;
                const semanaAno = row.semana ?? getISOWeekNumber(row.fecha);
                const sinStaff = !row.staff_agente_id || !staffIds.has(row.staff_agente_id);
                return (
                  <tr
                    key={row.fecha}
                    ref={esScroll ? filaActualRef : null}
                    style={{
                      borderTop: `1px solid ${isDark ? c.border : "#e8e8e0"}`,
                      opacity: isSaving ? 0.6 : 1,
                      background: getRowBg(idx, esSemanaActual),
                      boxShadow: esSemanaActual ? (isDark ? "inset 0 0 0 2px #14532d" : "inset 0 0 0 2px #15803d") : "none",
                    }}
                  >
                    <td style={{ padding: "8px 6px", color: textoCeldaTabla, fontWeight: 600, textAlign: "center" }}>
                      {semanaAno}
                    </td>
                    <td style={{ padding: "8px 6px", whiteSpace: "nowrap", color: textoCeldaTabla }}>
                      {fmtSabado(row.fecha)}
                      {row.editado_manual && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: textoEditado, fontWeight: 700 }}>editado</span>
                      )}
                    </td>
                    <td style={{ padding: "6px", minWidth: 140 }}>
                      <select
                        value={row.staff_agente_id || ""}
                        onChange={(e) => onStaffChange(row, e.target.value)}
                        disabled={isSaving || !puedeModificarStaff}
                        title={!puedeModificarStaff ? "No tienes permiso para modificar el staff" : undefined}
                        style={{
                          ...inp,
                          borderColor: sinStaff ? "#f59e0b" : (isDark ? c.border : "#d0d0c8"),
                          cursor: puedeModificarStaff ? "pointer" : "not-allowed",
                        }}
                      >
                        <option value="">—</option>
                        {staffOpts.map((a) => (
                          <option key={a.id} value={a.id}>{a.nombre}</option>
                        ))}
                      </select>
                      {sinStaff && row.staff_nombre && (
                        <div style={{ fontSize: 10, color: "#b45309", marginTop: 2 }}>{row.staff_nombre}</div>
                      )}
                    </td>
                    <td style={{ padding: "6px", minWidth: 140 }}>
                      <select
                        value={ag0}
                        onChange={(e) => onAgentChange(row, 0, e.target.value)}
                        disabled={isSaving || sinStaff || !puedeModificarAgentes}
                        title={!puedeModificarAgentes ? "No tienes permiso para modificar los agentes" : undefined}
                        style={{ ...inp, cursor: puedeModificarAgentes ? "pointer" : "not-allowed" }}
                      >
                        <option value="">—</option>
                        {agentOpts
                          .filter((a) => a.id !== row.staff_agente_id)
                          .map((a) => (
                            <option key={a.id} value={a.id}>{a.nombre}</option>
                          ))}
                      </select>
                    </td>
                    <td style={{ padding: "6px", minWidth: 140 }}>
                      <select
                        value={ag1}
                        onChange={(e) => onAgentChange(row, 1, e.target.value || null)}
                        disabled={isSaving || sinStaff || !puedeModificarAgentes}
                        title={!puedeModificarAgentes ? "No tienes permiso para modificar los agentes" : undefined}
                        style={{ ...inp, cursor: puedeModificarAgentes ? "pointer" : "not-allowed" }}
                      >
                        <option value="">(opcional)</option>
                        {agentOpts
                          .filter((a) => a.id !== row.staff_agente_id && a.id !== ag0)
                          .map((a) => (
                            <option key={a.id} value={a.id}>{a.nombre}</option>
                          ))}
                      </select>
                    </td>
                    <td style={{ padding: "8px 6px", color: textoCeldaTabla, fontSize: 11 }}>
                      {(row.no_trabajan || []).map((a) => a.nombre).join(", ") || "—"}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 24, textAlign: "center", color: c.textMuted }}>
                    No hay sábados en este año
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            padding: "12px 18px",
            borderTop: `1px solid ${isDark ? c.border : "#d8d8d0"}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 11, color: c.textMuted, maxWidth: 480 }}>
            Al guardar un sábado se alternan staff y agentes en los siguientes (desde esa fila).
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: `1px solid ${isDark ? c.border : "#c8c8be"}`,
              color: c.textMuted,
              borderRadius: 8,
              padding: "7px 14px",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
